import React, { FC, useCallback, useState, useEffect, useRef } from 'react';
import { Breakpoints } from '@bitcoin-portal/bitcoincom-pkg-theme';
import { GoArrowRight } from 'react-icons/go';
import { IoChevronDown } from 'react-icons/io5';
import Logo from './Logo';
import Hamburger from '../Hamburger';
import { handleRipple } from '../../../helpers/ripple';
import { useBreakpoint } from '../../../helpers/useBreakpoint';
import { remoteLog } from '../../../helpers/remoteLog';
import { useMPCWidget } from '../../../helpers/useMPCWidget';
import {
  Container,
  Content,
  NavbarContainer,
  NavbarWrapper,
  ItemsContainer,
  NavList,
  Item,
  BlankSpace,
  DesktopContainer,
  ResponsiveContainer,
  LoginButtonDesktop,
  StyledSearch,
  DropdownContainer,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from './styled';

interface Props {
  locale: string;
}

type DropdownState = {
  [key: string]: boolean;
};

interface NavbarItem {
  key: string;
  text: string;
  link?: string;
  openNewTab: boolean;
  children?: NavbarItem[];
}

const TopBar: FC<Props & Queries.SeoNavbarFragment> = ({ navbar, locale }) => {
  const [isMounted, setIsMounted] = useState(false);
  const realIsDesktop = useBreakpoint(Breakpoints.xl);
  const isDesktop = isMounted ? realIsDesktop : false;
  const navbarRef = useRef<HTMLDivElement>(null);
  const [dropdowns, setDropdowns] = useState<DropdownState>({
    hamburger: false,
  });
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  const { hasAccount, handleLoginClick } = useMPCWidget();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const closeDropdowns = () => {
    setDropdowns(prevState => ({
      ...prevState,
      hamburger: false,
    }));
    setOpenDropdownKey(null);
  };

  const handleButtonClick = async (text: string | null | undefined) => {
    await remoteLog(`User clicked on '${text}' item`);
  };

  const toggleDropdown = useCallback((name: string) => {
    setDropdowns(prevState =>
      Object.keys(prevState).reduce(
        (p, c) => ({
          ...p,
          [c]: c === name ? !prevState[c] : false,
        }),
        {},
      ),
    );
  }, []);

  const handleDropdownToggle = (key: string) => {
    setOpenDropdownKey(prevKey => (prevKey === key ? null : key));
  };

  const handleDropdownMouseEnter = (key: string) => {
    setOpenDropdownKey(key);
  };

  const handleDropdownMouseLeave = () => {
    setOpenDropdownKey(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
        closeDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderNavItem = (item: NavbarItem) => {
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <DropdownContainer
          key={item.key}
          onMouseEnter={() => handleDropdownMouseEnter(item.key)}
          onMouseLeave={handleDropdownMouseLeave}
        >
          <DropdownTrigger
            onClick={() => handleDropdownToggle(item.key)}
            $isOpen={openDropdownKey === item.key}
          >
            {item.text}
            <IoChevronDown
              size={14}
              style={{
                marginLeft: '4px',
                transition: 'transform 0.2s ease',
                transform: openDropdownKey === item.key ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </DropdownTrigger>
          <DropdownMenu $isOpen={openDropdownKey === item.key}>
            {item.children!.map(child => (
              <DropdownItem
                key={child.key}
                href={child.link ?? undefined}
                target={child.openNewTab ? '_blank' : '_self'}
                rel={child.openNewTab ? 'noopener noreferrer' : undefined}
                onClick={() => {
                  handleButtonClick(child.text);
                  setOpenDropdownKey(null);
                }}
              >
                {child.text}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </DropdownContainer>
      );
    }

    return (
      <Item
        key={item.key}
        href={item.link ?? undefined}
        target={item.openNewTab ? '_blank' : '_self'}
        rel={item.openNewTab ? 'noopener noreferrer' : undefined}
        onClick={() => handleButtonClick(item.text)}
      >
        {item.text}
      </Item>
    );
  };

  return (
    <Container>
      <Content ref={navbarRef}>
        <NavbarContainer>
          <NavbarWrapper>
            <Logo
              logo={navbar?.logoImage ?? undefined}
              locale={locale}
            />
            <ItemsContainer>
              <NavList>
                {(navbar?.navbarList ?? [])
                  .filter((item): item is NavbarItem => item !== null)
                  .map(item => renderNavItem(item))}
                <LoginButtonDesktop
                  className="pulse"
                  onClick={handleLoginClick}
                  onMouseEnter={handleRipple}
                  style={{ display: !hasAccount ? 'flex' : 'none' }}
                >
                  {navbar?.loginButton}
                  <GoArrowRight size={20} color="#1c1c1c" />
                </LoginButtonDesktop>
              </NavList>
            </ItemsContainer>
            {!isDesktop && <BlankSpace />}
            <StyledSearch locale="en" rebrand />
            <DesktopContainer>
              <div
                id="mpc-widget"
                style={{ display: hasAccount ? 'block' : 'none' }}
              />
            </DesktopContainer>
            <ResponsiveContainer>
              <Hamburger
                isOpen={dropdowns.hamburger}
                onClick={() => toggleDropdown('hamburger')}
                navbarList={(navbar?.navbarList ?? []).filter((item): item is NavbarItem => item !== null)}
                navbarMobileList={(navbar?.navbarMobileList ?? []).filter((item): item is NavbarItem => item !== null)}
                buttonData={{
                  buttonText: navbar?.buttonText,
                  buttonLink: navbar?.buttonLink,
                }}
              />
            </ResponsiveContainer>
          </NavbarWrapper>
        </NavbarContainer>
      </Content>
    </Container>
  );
};

export default TopBar;